/** Time-window argument editors: tpl 59 (trip time range, minutes UI over
 *  seconds wire) and tpls 67/68/69 (datetime/date/time within range with
 *  tpl-specific bound formats). */
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, DateTimePicker, TimePicker } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';

import { DT_RANGE_COMPARISONS, TPL } from '../ruleCatalog';
import {
  effectiveDtTz,
  minutesFromSeconds,
  parseDtBound,
  retzDtBound,
  secondsFromMinutes,
  serializeDtBound,
} from '../ruleSerialize';
import type { DtTz } from '../ruleSerialize';
import type { ArgEditorProps } from './common';
import { argsOf } from './common';

// ---------------------------------------------------------------------------
// tpl 59 — {future, future_delta, present, low_delta, high_delta, past,
//           past_delta} — deltas in SECONDS on the wire, minutes in the UI.
// ---------------------------------------------------------------------------

export function TripTimeRangeArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);

  const minutesField = (key: string, label: string, enabled: boolean) => (
    <TextField
      size="small"
      type="number"
      label={label}
      sx={{ width: 170 }}
      value={minutesFromSeconds(args[key])}
      disabled={disabled || !enabled}
      onChange={(e) => onArgsChange({ ...argsOf(rule), [key]: secondsFromMinutes(e.target.value) })}
    />
  );
  const toggle = (key: 'future' | 'present' | 'past', label: string) => (
    <FormControlLabel
      sx={{ width: 130, mr: 0 }}
      control={
        <Checkbox
          size="small"
          checked={Boolean(args[key])}
          disabled={disabled}
          onChange={(e) => onArgsChange({ ...argsOf(rule), [key]: e.target.checked })}
        />
      }
      label={label}
    />
  );

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {toggle('future', 'Future trips')}
        {minutesField('future_delta', 'Starting within (min)', Boolean(args.future))}
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {toggle('present', 'Ongoing trips')}
        {minutesField('low_delta', 'From start (min)', Boolean(args.present))}
        {minutesField('high_delta', 'To end (min)', Boolean(args.present))}
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {toggle('past', 'Past trips')}
        {minutesField('past_delta', 'Ended within (min)', Boolean(args.past))}
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// tpls 67/68/69 — {input, comparison, from, to, tz}
// from/to formats: 67 ISO-8601 (`…Z` in utc mode, no offset in local mode)
// · 68 YYYY-MM-DD · 69 HH:mm:ss. The pickers show the stored wall clock, never
// the browser's zone (see ruleSerialize `parseDtBound`).
// ---------------------------------------------------------------------------

export function DtRangeArgs({ rule, onArgsChange, disabled }: ArgEditorProps) {
  const args = argsOf(rule);
  const tpl = rule.tpl ?? TPL.DATETIME_WITHIN_RANGE;
  // No display-only fallback: a stored rule without `comparison` never matches
  // (the ruleset-2495 incident), so it shows as an empty, invalid choice.
  const comparison = typeof args.comparison === 'string' ? args.comparison : '';
  const showTo = (comparison || 'any_between').endsWith('_between');
  const tz = effectiveDtTz(tpl, args.tz);
  const tzLabel = tz === 'utc' ? 'UTC' : 'tour local time';
  const from = parseDtBound(tpl, args.from, tz);
  const to = parseDtBound(tpl, args.to, tz);
  const rangeInvalid = showTo && from && to && from.getTime() > to.getTime();

  const setBound = (key: 'from' | 'to', date: Date | null) =>
    onArgsChange({ ...args, [key]: serializeDtBound(tpl, date, tz) });

  // Switching the clock keeps the wall clock shown and rewrites the stored
  // bounds for the new mode (an offset in utc mode, none in local mode).
  const setTz = (next: DtTz) =>
    onArgsChange({
      ...args,
      tz: next,
      from: retzDtBound(tpl, tz, next, args.from),
      to: retzDtBound(tpl, tz, next, args.to),
    });

  const pickerProps = (key: 'from' | 'to') => ({
    value: key === 'from' ? from : to,
    onChange: (d: Date | null) => setBound(key, d),
    disabled,
    slotProps: {
      textField: { size: 'small' as const, sx: { width: 260 } },
      actionBar: { actions: ['clear', 'accept'] as ('clear' | 'accept')[] },
    },
    label: `${key === 'from' ? 'From' : 'To'} (${tzLabel})`,
  });

  const Picker =
    tpl === TPL.DATE_WITHIN_RANGE ? DatePicker : tpl === TPL.TIME_WITHIN_RANGE ? TimePicker : DateTimePicker;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={1.5}>
        <TextField
          size="small"
          label="Input path"
          fullWidth
          value={typeof args.input === 'string' ? args.input : ''}
          disabled={disabled}
          helperText='e.g. "participant.start_ts" or "order.bookings.:.start_ts" (":" iterates lists)'
          onChange={(e) => onArgsChange({ ...argsOf(rule), input: e.target.value })}
        />
        <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Comparison"
            sx={{ width: 300 }}
            value={comparison}
            disabled={disabled}
            error={!comparison}
            helperText={
              comparison
                ? '"any" and "all" behave the same on single values'
                : 'Choose how the values are compared — without one the rule never matches'
            }
            onChange={(e) => onArgsChange({ ...argsOf(rule), comparison: e.target.value })}
          >
            {DT_RANGE_COMPARISONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Times are in"
            sx={{ width: 200 }}
            value={tz}
            disabled={disabled}
            onChange={(e) => setTz(e.target.value as DtTz)}
          >
            <MenuItem value="utc">UTC</MenuItem>
            <MenuItem value="local">Tour local time</MenuItem>
          </TextField>
          <Picker {...pickerProps('from')} />
          {showTo && <Picker {...pickerProps('to')} />}
        </Stack>
        <Typography variant="caption" color={rangeInvalid ? 'error' : 'text.secondary'}>
          {rangeInvalid
            ? 'From must be before To.'
            : tz === 'utc'
              ? 'Values are converted to UTC before they are compared.'
              : "Values are compared on their own clock: the tour's time zone for tour times, UTC for system timestamps."}
        </Typography>
      </Stack>
    </LocalizationProvider>
  );
}
